import { useContext, useEffect, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
// Updated Icon Imports for reliability and modern standards
import { MdArrowCircleRight, MdHomeRepairService } from "react-icons/md";
import { FaTruckMoving } from "react-icons/fa";
import { IoCall } from "react-icons/io5";

// Image Imports (Ensure these paths match your actual asset structure)
import imgTerrazzo from "../assets/Terrazzo/banner8.jpg";
import imgRoofing from "../assets/LGS/banner6.jpg";
import imgProofing from "../assets/proofing1.jpeg";

export default function CoreServices(props) {
	const appContext = useContext(AppContext);
	const [ready, setReady] = useState(false);
	
	// Carousel State
	const [currentSlide, setCurrentSlide] = useState(0);
	const slides = [
		{ src: imgTerrazzo, alt: "Terrazzo Flooring", title: "Premium Finishes" },
		{ src: imgRoofing, alt: "Roofing Solutions", title: "Durable Roofing" },
		{ src: imgProofing, alt: "Waterproofing", title: "Advanced Proofing" },
	];

	useEffect(() => {
		setReady(true);
	}, []);

	// Auto-play carousel
	useEffect(() => {
		if (!ready) return;
		const timer = setInterval(() => {
			setCurrentSlide((prev) => (prev + 1) % slides.length);
		}, 4000);
		return () => clearInterval(timer);
	}, [ready, slides.length]);

	if (!ready) {
		return <div className="container mSupportLoading d-flex justify-content-center align-items-center min-vh-50">{getInlineLoader()}</div>;
	}

	return (
		<>
			{/* Custom Styles for Premium Feel */}
			<style>{`
				.premium-card {
					transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
					border: 1px solid rgba(0,0,0,0.05);
				}
				.premium-card:hover {
					transform: translateY(-8px);
					box-shadow: 0 20px 40px rgba(0,0,0,0.08) !important;
					border-color: rgba(0,0,0,0.1);
				}
				.carousel-container {
					position: relative;
					overflow: hidden;
					border-radius: 1rem;
					height: 100%;
					min-height: 350px;
				}
				.carousel-slide {
					position: absolute;
					top: 0;
					left: 0;
					width: 100%;
					height: 100%;
					opacity: 0;
					transition: opacity 1s ease-in-out;
					background-size: cover;
					background-position: center;
				}
				.carousel-slide.active {
					opacity: 1;
				}
				.carousel-overlay {
					position: absolute;
					bottom: 0;
					left: 0;
					width: 100%;
					padding: 2rem;
					background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
					color: white;
				}
				.step-circle {
					width: 40px;
					height: 40px;
					background: rgba(13, 110, 253);
					color: #ffffff;
					border-radius: 5px;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					font-weight: bold;
					margin-right: 10px;
					font-size:16px;
					font-weight:bold;
				}
				.btn-premium {
					transition: all 0.3s ease;
					position: relative;
					overflow: hidden;
				}
				.btn-premium:hover {
					transform: translateY(-2px);
					box-shadow: 0 10px 20px rgba(13, 110, 253, 0.3);
				}
			`}</style>

			<div className="MainBodySection py-5 bg-light-subtle mt-5">
				<div className="container">
					<div className="row g-3">
						{/* Header Section */}
						<div className="col-md-12 text-center py-2 bg-surface rounded">
							<p className="text-primary font-bold" style={{fontSize:"32px"}} >
								PROCUREMENT MADE SIMPLE
							</p>
							<p className="text-light font-semi-bold mx-auto lead" style={{ maxWidth: "700px", lineHeight: 1.8 }}>
								GROUP V goes beyond core solutions by offering transport, custom fabrication and flexible material sourcing, each available independently to make your procurement easier.
							</p>
						</div>

						{/* Why Simplify Section + Image Slider */}
						<div className="col-md-6 mb-4">
							<div className="bg-white rounded-4 shadow-sm p-3 h-100 border-0 premium-card">
								<p className="font-bold text-primary mb-2" style={{fontSize:"20px"}}>Why Simplify Procurement with GROUP V?</p>
								<p className="text-dark mb-2">
									Procurement isn't just about materials, it's about confidence, timing and convenience. GROUP V reduces complexity by offering additional services that can be engaged independently, giving you freedom to choose exactly what your project needs.
								</p>
								<ul className="list-unstyled text-muted">
									<li className="mb-3 d-flex align-items-start">
										<span className=" me-2">▪</span> 
										<div><strong className="text-primary">One trusted partner:</strong> Avoid juggling multiple vendors.</div>
									</li>
									<li className="mb-3 d-flex align-items-start">
										<span className=" me-2">▪</span> 
										<div><strong className="text-primary">Independent services:</strong> Transport, fabrication and extended supply can each be requested separately.</div>
									</li>
									<li className="mb-3 d-flex align-items-start">
										<span className=" me-2">▪</span> 
										<div><strong className="text-primary">Reduced risk:</strong> Fewer procurement headaches, more reliable outcomes.</div>
									</li>
								</ul>
							</div>
						</div>
						
						{/* Image Slider Section (Previously Empty) */}
						<div className="col-md-6 mb-4">
							<div className="carousel-container mShadow1">
								{slides.map((slide, index) => (
									<div 
										key={index} 
										className={`carousel-slide ${index === currentSlide ? 'active' : ''}`}
										style={{ backgroundImage: `url(${slide.src})` }}
									>
									</div>
								))}
								{/* Indicators */}
								<div className="position-absolute bottom-0 start-50 translate-middle-x mb-3 d-flex gap-2">
									{slides.map((_, idx) => (
										<button
											key={idx}
											onClick={() => setCurrentSlide(idx)}
											className={`border-0 rounded-pill p-0 ${idx === currentSlide ? 'bg-white' : 'bg-white bg-opacity-50'}`}
											style={{ width: '8px', height: '8px', cursor: 'pointer' }}
											aria-label={`Go to slide ${idx + 1}`}
										/>
									))}
								</div>
							</div>
						</div>

						{/* Services Section Header */}
						<div className="col-md-12 text-center mt-3 mb-0">
							<div className="vr d-none d-md-inline-block bg-primary me-3" style={{ height: '24px', verticalAlign: 'middle' }}></div>
							<p className="d-inline-block text-primary font-bold align-middle fs-3" >Our Additional Services</p>
							<div className="vr d-none d-md-inline-block bg-primary ms-3" style={{ height: '24px', verticalAlign: 'middle' }}></div>
						</div>
						
						{/* Service Cards */}
						<div className="col-md-4 mb-1">
							<div className="bg-white rounded-4 shadow-sm h-100 border-0 premium-card text-center py-4">
								<div className="d-inline-flex align-items-center justify-content-center bg-surface bg-opacity-10 text-primary rounded-3 mb-3" style={{ width: '64px', height: '64px' }}>
									<FaTruckMoving size={32} />
								</div>
								<p className="font-semi-bold text-primary" style={{fontSize:"18px"}}>Transportation</p>
								<p className="text-muted font-semi-bold px-2">"Reliable delivery, straight to your site."</p>
								<p className="text-dark small px-2" style={{ lineHeight: 1.6 }}>
									We handle logistics so you don't have to. With dependable transport services, your materials arrive on time, reducing delays and coordination stress.
								</p>
							</div>
						</div>
						<div className="col-md-4 mb-1">
							<div className="bg-white rounded-4 shadow-sm h-100 border-0 premium-card text-center py-4">
								<div className="d-inline-flex align-items-center justify-content-center bg-surface bg-opacity-10 text-primary rounded-3 mb-3" style={{ width: '64px', height: '64px' }}>
									<MdHomeRepairService size={32} />
								</div>
								<p className="font-semi-bold text-primary" style={{fontSize:"18px"}}>Custom Fabrication</p>
								<p className="text-muted font-semi-bold px-2">"Tailored components, built for you."</p>
								<p className="text-dark small px-2" style={{ lineHeight: 1.6 }}>
									Precision-made products that are durable and adaptable. From trunking and ductwork to aluminium louvers, façade panels, brackets, and trims.
								</p>
							</div>
						</div>
						<div className="col-md-4">
							<div className="bg-white rounded-4 shadow-sm h-100 border-0 premium-card text-center py-4">
								<div className="d-inline-flex align-items-center justify-content-center bg-surface bg-opacity-10 text-primary rounded-3 mb-3" style={{ width: '64px', height: '64px' }}>
									<MdArrowCircleRight size={32} />
								</div>
								<p className="font-semi-bold text-primary" style={{fontSize:"18px"}}>Supply of Other Materials</p>
								<p className="text-muted font-semi-bold px-2">"Flexibility beyond core solutions."</p>
								<p className="text-dark small px-2" style={{ lineHeight: 1.6 }}>
									Access diverse needs without juggling suppliers. We source tiles, specialty finishes, aluminium profiles, sanitary fittings and other project-specific items.
								</p>
							</div>
						</div>

						{/* How To Work Section */}
						<div className="col-md-12 text-center mb-0 mt-5">
							<p className="text-primary font-bold" style={{fontSize:"25px"}}>How To Work with Us</p>
						</div>
						<div className="col-md-12 mt-0">
							<div className="bg-white rounded-4 p-3 border-0 shadow-sm">
								<div className="row g-2">
									{[
										{ step: 1, title: "Share Your Requirements", desc: "Tell us what you need (transport, fabrication, materials). Provide project details & timelines." },
										{ step: 2, title: "Receive a Tailored Proposal", desc: "We prepare a clear plan covering scope, costs and schedules." },
										{ step: 3, title: "Confirm Engagement", desc: "Once agreed, we finalize the order and align on delivery or fabrication timelines." },
										{ step: 4, title: "Dependable Execution", desc: "We deliver on time, fabricate to spec, giving you confidence throughout." }
									].map((item) => (
										<div key={item.step} className="col-md-12">
											<div className="text-start h-100">
												<div className="d-flex gap-1">
													<div className="step-circle mb-1 flex-shrink-0">{item.step}</div>
													<p className="fw-bold text-dark mb-1">{item.title}</p>
												</div>
												<p className="text-muted small mb-3">{item.desc}</p>
											</div>
										</div>
									))}
								</div>
							</div>
						</div>

						{/* CTA Section */}
						<div className="col-md-12 text-center mb-2">
							<div className="bg-surface bg-opacity-10 rounded-4 p-3 border border-primary border-opacity-25">
								<p className="fw-bold text-primary mb-3" style={{fontSize:"25px"}}>Choose What You Need, When You Need It</p>
								<p className="text-light font-semi-bold mb-4 mx-auto" style={{ maxWidth: "600px" }}>
									Ready to simplify your procurement? Whether you need transport, custom fabrication, or additional materials, we're here to make it easy.
								</p>
								<div className="d-flex justify-content-center gap-3 flex-wrap">
									<button 
										className="btn btn-primary" 
										onClick={() => appContext.navTo({item:'contact_us'})}
									>
										Call Us Today <IoCall className="ms-2 mb-1" size={20} />
									</button>
									<button 
										className="btn btn-secondary" 
										onClick={() => appContext.navTo({item:'contact_us'})}
									>
										Solve Your Project Challenges
									</button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}